import { useMemo } from 'react'

import { Box, Center, Flex, Stack, Text } from '@chakra-ui/react'
import { TextWithBar } from 'src/components/TextWithBar/TextWithBar'
import {
  ACCEPT_TRIAL_SHARED_FILE_TYPES,
  getFileTypeLabel,
} from 'src/constants/acceptableFileTypes'
import {
  useAuthenticatedAccount,
  usePermission,
  useSelectedTrial,
} from 'src/features/auth/context'
import { FileUploadComponent } from 'src/features/misc/components'
import {
  fileSizeWithUnit,
  SavedFileItem,
} from 'src/features/misc/utils/fileUploadUtils'
import { GetTrialHospitalSharedFilesDocument } from 'src/lib/gql-client'
import { useGqlQuerySWR } from 'src/lib/gql-client/request'
import { PERMISSIONS } from 'src/lib/permission'

import { getSharedUrl } from './getSharedUrl'
import { DashboardWidget } from '../DashboardWidget/DashboardWidget'
import {
  HospitalSharedFile,
  HospitalSharedFileTable,
} from '../HospitalSharedFileTable/HospitalSharedFileTable'

type Props = {
  selectedTrialHospitalUidWithDM?: string
}

export const HospitalSharedFileWidget: React.FC<Props> = ({
  selectedTrialHospitalUidWithDM,
}: Props) => {
  const { account } = useAuthenticatedAccount()
  const { hasPermission } = usePermission()
  const { selectedTrial } = useSelectedTrial()

  /**
   * 治験共通ファイルのアップロードや参照に利用する医療機関のUID
   * 実施医療機関でのログイン → 実施医療機関のUID
   * パートナー施設でのログイン → パートナー施設のUID
   * DMでのログイン → 選択中の実施医療機関のUID
   */
  const selectedTrialHospitalUid = useMemo(() => {
    if (account.selectedTrialHospitalRole !== null) {
      return account.selectedTrialHospitalUid!
    }

    // DMでのログイン時にはPropsに値が確実に渡される
    return selectedTrialHospitalUidWithDM!
  }, [
    account.selectedTrialHospitalRole,
    account.selectedTrialHospitalUid,
    selectedTrialHospitalUidWithDM,
  ])

  const {
    data: {
      trialHospital: { trialHospitalSharedFiles: sharedFiles },
    },
    mutate,
  } = useGqlQuerySWR(
    GetTrialHospitalSharedFilesDocument,
    {
      trialHospitalUid: selectedTrialHospitalUid,
    },
    {
      revalidateIfStale: false,
      revalidateOnFocus: false,
    },
  )

  const {
    defaultFileItems,
    hospitalSharedFiles,
  }: {
    defaultFileItems: SavedFileItem[]
    hospitalSharedFiles: HospitalSharedFile[]
  } = useMemo(() => {
    const defaultFileItems: SavedFileItem[] = sharedFiles.map(file => {
      const { uploadedFile } = file

      return {
        isNew: false,
        isValid: true,
        uid: uploadedFile.uid,
        fileName: uploadedFile.name,
        fileSize: uploadedFile.size,
        fileUrl: uploadedFile.url,
        fileExtension: uploadedFile.extension,
        savedAt: uploadedFile.savedAt,
      }
    })

    const hospitalSharedFiles: HospitalSharedFile[] = sharedFiles.map(file => {
      return {
        fileName: file.uploadedFile.name,
        fileSize: fileSizeWithUnit(file.uploadedFile.size),
        fileExtension: file.uploadedFile.extension,
        downloadUrl: file.uploadedFile.url,
        sharedLink: getSharedUrl({
          trialUid: selectedTrial.uid,
          trialHospitalSharedFileUid: file.trialHospitalSharedFileUid,
          fileExtension: file.uploadedFile.extension,
        }),
      }
    })

    return { defaultFileItems, hospitalSharedFiles }
  }, [selectedTrial.uid, sharedFiles])

  const canUploadFile =
    hasPermission(PERMISSIONS.Trialtop_FileUpload) &&
    account.selectedTrialHospitalRole === 'Main'

  return (
    <DashboardWidget maxH="500px">
      <Stack spacing="4" h="full">
        <Flex justifyContent="space-between">
          <TextWithBar fontWeight="bold">共有資料</TextWithBar>
          {canUploadFile ? (
            <FileUploadComponent
              defaultFileItems={defaultFileItems}
              acceptType={[...ACCEPT_TRIAL_SHARED_FILE_TYPES]}
              nativeDialogAcceptType={[...ACCEPT_TRIAL_SHARED_FILE_TYPES]}
              uploadOptions={{
                trialHospitalSharedFiles: {
                  trialHospitalUid: selectedTrialHospitalUid,
                },
              }}
              isCertifiedCopy={false}
              onUploaded={() => mutate()}
              withoutNote
            />
          ) : null}
        </Flex>
        {hospitalSharedFiles.length > 0 ? (
          <Box flex="1" overflow="auto">
            <HospitalSharedFileTable sharedFiles={hospitalSharedFiles} />
          </Box>
        ) : (
          <Center flexGrow={1}>
            <Stack spacing="2" justify="center" align="center">
              <Text color="gray.400" fontWeight="bold">
                ファイルはありません
              </Text>
              <Box>
                <Text color="gray.400" fontSize="sm" textAlign="center">
                  ※アップロード可能なファイル形式
                </Text>
                <Text color="gray.400" fontSize="sm" textAlign="center">
                  （
                  {ACCEPT_TRIAL_SHARED_FILE_TYPES.map(t =>
                    getFileTypeLabel(t).toLowerCase(),
                  ).join('・')}
                  ）
                </Text>
              </Box>
            </Stack>
          </Center>
        )}
      </Stack>
    </DashboardWidget>
  )
}
